import { useEffect, useRef, RefObject } from "react";

/**
 * useInfiniteScroll Hook
 *
 * Detects when user scrolls near the bottom of a container and triggers a callback
 * Useful for implementing infinite scroll/load more functionality
 *
 * @param callback - Function to call when user reaches the bottom
 * @param hasMore - Whether there's more data to load
 * @param isLoading - Whether data is currently being loaded
 * @param threshold - Distance from bottom (in pixels) to trigger callback (default: 300px)
 * @returns ref - Ref to attach to the scrollable container (optional, uses window if not provided)
 */
export function useInfiniteScroll(
  callback: () => void,
  hasMore: boolean,
  isLoading: boolean,
  threshold: number = 300
): RefObject<HTMLDivElement> {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || isLoading) return;

    const handleScroll = () => {
      // Calculate scroll position
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      // Check if user is near bottom
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom < threshold) {
        callback();
      }
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [callback, hasMore, isLoading, threshold]);

  return containerRef;
}
